import React, {
    Fragment,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import {
    BoltOutlined as BoltOutlinedIcon,
    Close as CloseIcon,
    HttpOutlined as HttpOutlinedIcon,
    Refresh as RefreshIcon,
} from "@mui/icons-material";
// MUI Components
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    Modal,
    Paper,
    Stack,
    Switch,
    Tooltip,
    Typography,
} from "@mui/material";

import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { allExpanded, defaultStyles, JsonView } from "react-json-view-lite";
import { useParams } from "react-router-dom";

import EmptyState from "components/atoms/EmptyState";
import { GenericTabs } from "components/GenericTabs";
import { ConfirmationModal } from "components/organisms/ConfirmationModal";
import GenericTable from "components/organisms/GenericTable";

import PaginationWithInfo from "../../../../components/PaginationWithInfo";
import {
    KeywordInputRow,
    SectionBlock,
    SectionTitle,
    StyledKeywordInput,
} from "../GeneralSettings/GeneralSettings.styled";
import {
    createRequestsColumns,
    createTestEventsColumns,
} from "./TestEvents.helpers";

const TestEvents = ({
    language,

    activeTab,
    eventsList,
    requestsList,
    trackerTestEventCode,
    eventDetails,

    dispatchGetTrackerTestEventCode,
    dispatchPostTrackerTestEventCode,
    dispatchPutTrackerTestEventCode,
    dispatchDeleteTrackerTestEventCode,
    dispatchGetTestEventDetail,
    dispatchGetTestEventsOrRequests,

    dispatchSetActiveTab,

    dispatchSelectPage,
    dispatchSetTrackerId,
    dispatchClearState,
}) => {
    const [inputData, setInputData] = useState("");
    const [eventDetailsModal, setEventDetailsModal] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const interval = useRef();
    const autoRefreshRef = useRef(false);
    const params = useParams();

    const createFetchHelper = useCallback(
        (action, page) => {
            action && action();

            clearInterval(interval.current);
            if (page === 0 && autoRefreshRef.current) {
                interval.current = setInterval(() => {
                    if (autoRefreshRef.current) {
                        dispatchGetTestEventsOrRequests();
                    }
                }, 15000);
            }
        },
        [dispatchGetTestEventsOrRequests],
    );

    const handleInputChange = (e) => {
        setInputData(e.target.value);
    };

    const handleCreateTestEventCode = () => {
        dispatchPostTrackerTestEventCode({
            name: inputData,
        });
        setInputData("");
    };

    const handleUpdateTrackerTestEventCode = async () => {
        dispatchPutTrackerTestEventCode({
            id: trackerTestEventCode?.id,
            name: inputData,
        });
    };

    const handleDeleteTrackerTestEventCode = async () => {
        dispatchDeleteTrackerTestEventCode(trackerTestEventCode?.id);
    };

    useEffect(() => {
        dispatchSetTrackerId(params.id);
        dispatchGetTrackerTestEventCode();

        createFetchHelper(() => {
            dispatchGetTestEventsOrRequests();
        }, 0);
        return () => {
            dispatchClearState();
            clearInterval(interval.current);
        };
    }, [
        dispatchSetTrackerId,
        params.id,
        dispatchGetTrackerTestEventCode,
        createFetchHelper,
        dispatchGetTestEventsOrRequests,
        dispatchClearState,
    ]);

    useEffect(() => {
        setInputData(trackerTestEventCode?.data?.name || "");
    }, [trackerTestEventCode]);

    useEffect(() => {
        autoRefreshRef.current = autoRefresh;
    }, [autoRefresh]);

    useEffect(() => {
        clearInterval(interval.current);
        if (autoRefresh) {
            interval.current = setInterval(() => {
                if (autoRefreshRef.current) {
                    dispatchGetTestEventsOrRequests();
                }
            }, 15000);
        }
        return () => clearInterval(interval.current);
    }, [autoRefresh, dispatchGetTestEventsOrRequests]);

    useEffect(() => {
        if (
            (eventsList?.isError || requestsList?.isError) &&
            autoRefreshRef.current
        ) {
            setAutoRefresh(false);
        }
    }, [eventsList?.isError, requestsList?.isError]);

    return (
        <>
            <ConfirmationModal
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => {
                    handleDeleteTrackerTestEventCode();
                    setConfirmOpen(false);
                }}
                title={
                    language?.delete_swal_modal?.are_you_sure ?? "Are you sure?"
                }
                message="This action cannot be undone."
                confirmColor="error"
            />
            {trackerTestEventCode?.isLoading ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                >
                    <CircularProgress size={24} color="primary" />
                </Box>
            ) : (
                <>
                    <SectionBlock>
                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                                flexDirection: "column",
                                marginBottom: "20px",
                            }}
                        >
                            <SectionTitle>Test Event Code</SectionTitle>
                            <Box>
                                You can perform pixel tests by defining
                                test_event_code to your tracker.
                            </Box>
                        </Box>

                        <KeywordInputRow>
                            <StyledKeywordInput
                                size="small"
                                variant="outlined"
                                placeholder={"Enter test event code"}
                                value={inputData}
                                onChange={(e) => setInputData(e.target.value)}
                            />

                            {!!trackerTestEventCode.data ? (
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    style={{
                                        marginLeft: 0,
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={
                                            handleUpdateTrackerTestEventCode
                                        }
                                        disabled={
                                            trackerTestEventCode?.isUpdating
                                        }
                                        sx={{
                                            textTransform: "none",
                                            marginLeft: 0,
                                        }}
                                        style={{
                                            minWidth: "unset !important",
                                        }}
                                        startIcon={
                                            trackerTestEventCode?.isUpdating ? (
                                                <CircularProgress
                                                    size={20}
                                                    color="inherit"
                                                />
                                            ) : null
                                        }
                                    >
                                        {trackerTestEventCode?.isUpdating
                                            ? "UPDATING..."
                                            : (language?.buttons?.update ??
                                              "UPDATE")}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        disabled={
                                            trackerTestEventCode?.isDeleting
                                        }
                                        onClick={() => setConfirmOpen(true)}
                                        sx={{
                                            textTransform: "none",
                                            minWidth: "fit-content !important",
                                        }}
                                    >
                                        {trackerTestEventCode?.isDeleting ? (
                                            <CircularProgress
                                                size={20}
                                                color="inherit"
                                            />
                                        ) : (
                                            <FontAwesomeIcon
                                                icon={faTrashAlt}
                                            />
                                        )}
                                    </Button>
                                </Stack>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleCreateTestEventCode}
                                    sx={{
                                        textTransform: "none",
                                    }}
                                    disabled={
                                        !inputData ||
                                        trackerTestEventCode?.isSaving
                                    }
                                    style={{
                                        marginLeft: 0,
                                        minWidth: "unset !important",
                                    }}
                                    startIcon={
                                        trackerTestEventCode?.isSaving ? (
                                            <CircularProgress
                                                size={20}
                                                color="inherit"
                                            />
                                        ) : null
                                    }
                                >
                                    {trackerTestEventCode?.isSaving
                                        ? "SAVING..."
                                        : "SAVE"}
                                </Button>
                            )}
                        </KeywordInputRow>
                    </SectionBlock>
                    <SectionBlock>
                        <GenericTabs
                            defaultTab={activeTab}
                            variant="scrollable"
                            showBadges={false}
                            contentPadding={3}
                            onTabChange={(newValue) => {
                                dispatchSetActiveTab(newValue);
                                createFetchHelper(() => {
                                    dispatchGetTestEventsOrRequests();
                                }, 0);
                            }}
                            tabs={[
                                {
                                    label: "Events",
                                    value: "events",
                                    content: (
                                        <Box>
                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                sx={{ mb: 1 }}
                                            >
                                                <SectionTitle>
                                                    Test Your Events
                                                </SectionTitle>
                                                <Tooltip
                                                    title={
                                                        autoRefresh
                                                            ? "Auto refresh is enabled. Data refreshes every 15 seconds. Turn off to stop automatic fetching."
                                                            : "Enable to automatically refresh data every 15 seconds"
                                                    }
                                                    arrow
                                                    placement="top"
                                                >
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        spacing={0.5}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            fontWeight={500}
                                                            whiteSpace="nowrap"
                                                        >
                                                            Auto Refresh
                                                        </Typography>
                                                        <Switch
                                                            checked={
                                                                autoRefresh
                                                            }
                                                            onChange={(e) =>
                                                                setAutoRefresh(
                                                                    e.target
                                                                        .checked,
                                                                )
                                                            }
                                                            size="small"
                                                        />
                                                    </Stack>
                                                </Tooltip>
                                                <Tooltip
                                                    title="Refresh the test events list to get the latest data"
                                                    arrow
                                                    placement="top"
                                                >
                                                    <IconButton
                                                        color="primary"
                                                        size="small"
                                                        onClick={() => {
                                                            createFetchHelper(
                                                                () => {
                                                                    dispatchGetTestEventsOrRequests();
                                                                },
                                                                eventsList.page,
                                                            );
                                                        }}
                                                    >
                                                        <RefreshIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>

                                            <Box marginBottom={1}>
                                                Check that your events are set
                                                up correctly. Test events are
                                                meant to help you validate that
                                                your website, app and server
                                                events are being received as
                                                intended.
                                            </Box>

                                            <Alert
                                                severity={
                                                    !!eventsList?.data?.length
                                                        ? "success"
                                                        : "primary"
                                                }
                                                sx={{ mb: 3 }}
                                                icon={
                                                    <Box
                                                        sx={{
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: "50%",
                                                            backgroundColor:
                                                                !!eventsList
                                                                    ?.data
                                                                    ?.length
                                                                    ? "success.main"
                                                                    : "warning.main",
                                                            animation:
                                                                "pulse 2s infinite",
                                                        }}
                                                    />
                                                }
                                            >
                                                {!!eventsList?.data?.length
                                                    ? "Receiving activity"
                                                    : "Waiting activity"}
                                            </Alert>

                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    position: "relative",
                                                }}
                                            >
                                                {eventsList?.isListLoading && (
                                                    <Box
                                                        sx={{
                                                            position:
                                                                "absolute",
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            backgroundColor:
                                                                "rgba(255, 255, 255, 0.8)",
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            zIndex: 1,
                                                            backdropFilter:
                                                                "blur(4px)",
                                                        }}
                                                    >
                                                        <CircularProgress color="primary" />
                                                    </Box>
                                                )}

                                                <GenericTable
                                                    columns={createTestEventsColumns(
                                                        {
                                                            dispatchGetTestEventDetail,
                                                            setEventDetailsModal,
                                                        },
                                                    )}
                                                    rows={
                                                        eventsList?.data || []
                                                    }
                                                    getRowKey={(row) => row.id}
                                                    emptyState={
                                                        <EmptyState
                                                            noBorder
                                                            icon={
                                                                <BoltOutlinedIcon />
                                                            }
                                                            title="No test events found"
                                                            description="Trigger an event from your source to see it show up here."
                                                        />
                                                    }
                                                />
                                            </Paper>

                                            <PaginationWithInfo
                                                data={eventsList}
                                                itemType="events"
                                                onPageChange={(page) => {
                                                    dispatchSelectPage(page);
                                                    createFetchHelper(() => {
                                                        dispatchGetTestEventsOrRequests();
                                                    }, page);
                                                }}
                                            />
                                        </Box>
                                    ),
                                },
                                {
                                    label: "Requests",
                                    value: "requests",
                                    content: (
                                        <Box>
                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                sx={{ mb: 1 }}
                                            >
                                                <SectionTitle>
                                                    Test Your Requests
                                                </SectionTitle>
                                                <Tooltip
                                                    title={
                                                        autoRefresh
                                                            ? "Auto refresh is enabled. Data refreshes every 15 seconds. Turn off to stop automatic fetching."
                                                            : "Enable to automatically refresh data every 15 seconds"
                                                    }
                                                    arrow
                                                    placement="top"
                                                >
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        spacing={0.5}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            fontWeight={500}
                                                            whiteSpace="nowrap"
                                                        >
                                                            Auto Refresh
                                                        </Typography>
                                                        <Switch
                                                            checked={
                                                                autoRefresh
                                                            }
                                                            onChange={(e) =>
                                                                setAutoRefresh(
                                                                    e.target
                                                                        .checked,
                                                                )
                                                            }
                                                            size="small"
                                                        />
                                                    </Stack>
                                                </Tooltip>
                                                <Tooltip
                                                    title="Refresh the test requests list to get the latest data"
                                                    arrow
                                                    placement="top"
                                                >
                                                    <IconButton
                                                        color="primary"
                                                        size="small"
                                                        onClick={
                                                            dispatchGetTestEventsOrRequests
                                                        }
                                                    >
                                                        <RefreshIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>

                                            <Box marginBottom={1}>
                                                Check that your events are set
                                                up correctly. Test events are
                                                meant to help you validate that
                                                your website, app and server
                                                events are being received as
                                                intended.
                                            </Box>

                                            <Alert
                                                severity={
                                                    !!requestsList?.data?.length
                                                        ? "success"
                                                        : "primary"
                                                }
                                                sx={{ mb: 3 }}
                                                icon={
                                                    <Box
                                                        sx={{
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: "50%",
                                                            backgroundColor:
                                                                !!requestsList
                                                                    ?.data
                                                                    ?.length
                                                                    ? "success.main"
                                                                    : "primary.main",
                                                            animation:
                                                                "pulse 2s infinite",
                                                        }}
                                                    />
                                                }
                                            >
                                                {!!requestsList?.data?.length
                                                    ? "Receiving activity"
                                                    : "Waiting activity"}
                                            </Alert>

                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    position: "relative",
                                                }}
                                            >
                                                {requestsList?.isListLoading && (
                                                    <Box
                                                        sx={{
                                                            position:
                                                                "absolute",
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            backgroundColor:
                                                                "rgba(255, 255, 255, 0.8)",
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            zIndex: 1,
                                                            backdropFilter:
                                                                "blur(4px)",
                                                        }}
                                                    >
                                                        <CircularProgress color="primary" />
                                                    </Box>
                                                )}

                                                <GenericTable
                                                    columns={createRequestsColumns()}
                                                    rows={
                                                        requestsList?.data || []
                                                    }
                                                    getRowKey={(row) => row.id}
                                                    emptyState={
                                                        <EmptyState
                                                            noBorder
                                                            icon={
                                                                <HttpOutlinedIcon />
                                                            }
                                                            title="No test requests found"
                                                            description="Requests sent to this tracker will show up here."
                                                        />
                                                    }
                                                />
                                            </Paper>

                                            <PaginationWithInfo
                                                data={requestsList}
                                                itemType="requests"
                                                onPageChange={(page) => {
                                                    dispatchSelectPage(page);
                                                    createFetchHelper(() => {
                                                        dispatchGetTestEventsOrRequests();
                                                    }, page);
                                                }}
                                            />
                                        </Box>
                                    ),
                                },
                            ]}
                        />
                    </SectionBlock>
                </>
            )}

            <Modal
                open={eventDetailsModal}
                onClose={() => setEventDetailsModal(false)}
                aria-labelledby="event-details-modal"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "90vw",
                        maxWidth: 800,
                        maxHeight: "90vh",
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 0,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Box
                        sx={{
                            p: 3,
                            borderBottom: 1,
                            borderColor: "divider",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Typography variant="h6" component="h2">
                            {language?.test_events?.events_details ??
                                "Event Details"}
                        </Typography>
                        <IconButton onClick={() => setEventDetailsModal(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box
                        sx={{
                            p: 3,
                            flex: 1,
                            overflow: "auto",
                            maxHeight: "75vh",
                        }}
                    >
                        {!!eventDetails?.data && !eventDetails?.isLoading && (
                            <JsonView
                                data={eventDetails?.data}
                                shouldExpandNode={allExpanded}
                                style={defaultStyles}
                            />
                        )}
                        {eventDetails?.isLoading && (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="200px"
                            >
                                <CircularProgress color="primary" />
                            </Box>
                        )}
                    </Box>

                    <Box
                        sx={{
                            p: 3,
                            borderTop: 1,
                            borderColor: "divider",
                            display: "flex",
                            justifyContent: "flex-end",
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setEventDetailsModal(false)}
                            sx={{ textTransform: "none" }}
                        >
                            {language?.buttons?.close ?? "Close"}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default TestEvents;
